/**
 * Admin — top-level orchestrator for the operator control room.
 *
 * Owns:
 *   - top-level data state (drivers / passengers / rides / configs / cycles)
 *   - the dual pollers (4s fast loop for ops data, 15s slow loop for health)
 *   - the realtime WebSocket subscription (driver location + ride-health alerts)
 *   - tab selection + deep-link state sync from <Link state={{tab:"..."}}>
 *   - the role gate (non-admin role lands on their own dashboard)
 *
 * Mutations + tab-nav presentation + tab definitions live in sibling
 * modules under `pages/admin/`. This file is intentionally small so each
 * panel can evolve without touching the orchestrator.
 *
 * Phase 2 P2 #2 decomposition · 2026-06: extracted `tabsConfig.js`,
 * `AdminTabs.jsx`, `useAdminMutations.js`. Behaviour byte-identical
 * to the pre-decomposition orchestrator.
 */
import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api, tokenStore } from "../lib/api";
import { Header } from "../components/Header";
import { useRealtime } from "../hooks/useRealtime";
import { toast } from "sonner";

import { MapPanel } from "./admin/MapPanel";
import { RidesPanel } from "./admin/RidesPanel";
import { FleetPanel } from "./admin/FleetPanel";
import { PassengersPanel } from "./admin/PassengersPanel";
import { SettingsPanel } from "./admin/SettingsPanel";
import { RideHealthPanel } from "./admin/RideHealthPanel";
import { SystemHealthPanel } from "./admin/SystemHealthPanel";
import { KpiDashboardPanel } from "./admin/KpiDashboardPanel";
import { FatiguePanel } from "./admin/FatiguePanel";
import { CompliancePanel } from "./admin/CompliancePanel";
import { CorporatePanel } from "./admin/CorporatePanel";
import { SubscriptionsPanel } from "./admin/SubscriptionsPanel";
import { WhatsAppInboxPanel } from "./admin/WhatsAppInboxPanel";
import { ConfirmationLogPanel } from "./admin/ConfirmationLogPanel";
import { MissedEnquiriesPanel } from "./admin/MissedEnquiriesPanel";
import { ApprovalsPanel } from "./admin/ApprovalsPanel";
import { CommsPanel } from "./admin/CommsPanel";
import { OnboardingWizard } from "./admin/OnboardingWizard";
import { ImportsPanel } from "./admin/ImportsPanel";
import { LaunchResetPanel } from "./admin/LaunchResetPanel";
import { OperatorProvisioningWizard } from "./admin/OperatorProvisioningWizard";
import { CorporateApplicationsPanel } from "./admin/CorporateApplicationsPanel";
import { LaunchReadinessPanel } from "./admin/LaunchReadinessPanel";
import { CopilotPanel } from "./admin/CopilotPanel";
import { AiBookingPanel } from "./admin/AiBookingPanel";
import { VoiceTuningPanel } from "./admin/VoiceTuningPanel";
import { DemoPanel } from "./admin/DemoPanel";
import { SafetyPanel } from "./admin/SafetyPanel";
import { SHOW_LAUNCH_TOOLS, SHOW_DEMO_TOOLS } from "./admin/tabsConfig";
import { GuidedTour } from "./admin/GuidedTour";
import { PresentationMode } from "./admin/PresentationMode";
import PresentationWelcome from "./admin/PresentationWelcome";
import { Compass, Presentation as PresentationIcon, AlertTriangle, ShieldAlert, X, Sparkles, Building2 } from "lucide-react";
import { useAnyModalOpen } from "../hooks/useAnyModalOpen";

import { AdminStatsBar, AdminSectionBar } from "./admin/AdminTabs";
import { AdminHomeDashboard } from "./admin/AdminHomeDashboard";
import { useAdminMutations } from "./admin/useAdminMutations";
import { FinancialPrivacyProvider } from "../lib/financialPrivacy";

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  // Allow cross-tab deep-links from anywhere in the admin surface to
  // open a specific Admin tab — e.g. the OnboardingWizard's
  // drivers-open-imports / passengers-open-imports buttons, or the
  // ImportsPanel CommitResult 'View in Fleet → ' links. Other panels
  // navigate via <Link to="/admin" state={{tab:"imports"}} />; without
  // this hook the location update would be a no-op when the user is
  // already on /admin.
  const location = useLocation();

  const [tab, setTab] = useState(location.state?.tab || "home");
  const [tourOpen, setTourOpen] = useState(false);
  const [presentOpen, setPresentOpen] = useState(false);
  const [presentInitialProfile, setPresentInitialProfile] = useState(null);
  // First-login Presentation Selector — Demo Mode only, shown once per browser.
  const [welcomeOpen, setWelcomeOpen] = useState(
    () => SHOW_DEMO_TOOLS && localStorage.getItem("demoWelcomeSeen") !== "1"
  );
  const markWelcomeSeen = () => { try { localStorage.setItem("demoWelcomeSeen", "1"); } catch (_) {} };
  const handleWelcomePick = (profileId) => {
    markWelcomeSeen();
    setWelcomeOpen(false);
    setPresentInitialProfile(profileId);
    setPresentOpen(true);
  };
  const handleWelcomeExplore = () => { markWelcomeSeen(); setWelcomeOpen(false); };

  // ── Kiosk Mode (Demo only): auto-return to the welcome selector when idle ──
  const [presentationPlaying, setPresentationPlaying] = useState(false);
  const [demoExpanded, setDemoExpanded] = useState(false);
  const anyModalOpen = useAnyModalOpen();
  const [kioskEnabled, setKioskEnabled] = useState(() => localStorage.getItem("demoKioskEnabled") === "1");
  const [kioskTimeoutMin, setKioskTimeoutMin] = useState(() => Number(localStorage.getItem("demoKioskTimeoutMin")) || 4);
  const [kioskReset, setKioskReset] = useState(() => localStorage.getItem("demoKioskReset") === "1");
  useEffect(() => { try { localStorage.setItem("demoKioskEnabled", kioskEnabled ? "1" : "0"); } catch (_) {} }, [kioskEnabled]);
  useEffect(() => { try { localStorage.setItem("demoKioskTimeoutMin", String(kioskTimeoutMin)); } catch (_) {} }, [kioskTimeoutMin]);
  useEffect(() => { try { localStorage.setItem("demoKioskReset", kioskReset ? "1" : "0"); } catch (_) {} }, [kioskReset]);

  useEffect(() => {
    if (!SHOW_DEMO_TOOLS || !kioskEnabled) return;
    // Don't time out while a presentation is actively playing or the welcome is up.
    if (presentationPlaying || welcomeOpen) return;
    let timer = null;
    const onIdle = () => {
      setTourOpen(false);
      setPresentOpen(false);
      setPresentInitialProfile(null);
      setTab("home");
      if (kioskReset) { api.post("/admin/demo/reset").catch(() => {}); }
      setWelcomeOpen(true);
    };
    const arm = () => { if (timer) clearTimeout(timer); timer = setTimeout(onIdle, kioskTimeoutMin * 60000); };
    const evts = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"];
    evts.forEach((e) => window.addEventListener(e, arm, { passive: true }));
    arm();
    return () => { if (timer) clearTimeout(timer); evts.forEach((e) => window.removeEventListener(e, arm)); };
  }, [kioskEnabled, kioskTimeoutMin, kioskReset, presentationPlaying, welcomeOpen]);
  // Optional sub-tab to open inside the Communications panel (e.g. when the
  // home "Recruitment" tile is tapped). Resets on each open.
  const [commsInitial, setCommsInitial] = useState("campaigns");
  useEffect(() => {
    if (location.state?.tab) setTab(location.state.tab);
    // Keyed on location.key so a repeat-click from the same source
    // re-fires the effect (otherwise React de-dupes identical state).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  /* ============================ Data state ============================ */
  const [stats, setStats] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [passengers, setPassengers] = useState([]);
  const [rides, setRides] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [fareConfig, setFareConfig] = useState(null);
  const [surgeConfig, setSurgeConfig] = useState(null);
  const [cycleConfig, setCycleConfig] = useState(null);
  const [cycles, setCycles] = useState([]);
  const [fixedRoutes, setFixedRoutes] = useState([]);
  const [accountRides, setAccountRides] = useState([]);
  const [fleetTab, setFleetTab] = useState("drivers");
  const [settingsTab, setSettingsTab] = useState("fare");
  const [healthData, setHealthData] = useState(null);
  const [fatigueAlert, setFatigueAlert] = useState(null);
  const [complianceAlert, setComplianceAlert] = useState(null);
  const [inboxUnread, setInboxUnread] = useState(0);
  const [sosActive, setSosActive] = useState({ active: 0, emergency: 0 });
  const [sosSignal, setSosSignal] = useState(null);
  const [sosBanner, setSosBanner] = useState(null);
  const [openSosId, setOpenSosId] = useState(null);
  const [webReqFocus, setWebReqFocus] = useState(null);
  const [webAckSignal, setWebAckSignal] = useState(0);
  const chimeRef = useRef(null);

  // Claim ownership of a website request from the toast. Non-blocking; surfaces
  // a clear notice if another dispatcher already owns it.
  const acknowledgeWebRequest = useCallback(async (sessionId, name) => {
    try {
      await api.post(`/admin/ai-booking/web-requests/${sessionId}/acknowledge`, {});
      toast.success(`You're now handling ${name || "this request"}.`);
      setWebAckSignal((n) => n + 1);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (detail && typeof detail === "object" ? detail.message : detail) || "Couldn't acknowledge this request.";
      toast.message(msg);
      setWebAckSignal((n) => n + 1);
    }
  }, []);

  const playSosChime = useCallback(() => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ac = new Ctx(); const o = ac.createOscillator(); const g = ac.createGain();
      o.type = "square"; o.frequency.value = 880; o.connect(g); g.connect(ac.destination);
      g.gain.setValueAtTime(0.18, ac.currentTime); o.start();
      o.frequency.setValueAtTime(660, ac.currentTime + 0.2);
      o.stop(ac.currentTime + 0.45);
    } catch (e) { /* audio best-effort */ }
  }, []);

  const acknowledgeSos = useCallback(async (sosId) => {
    // Optimistically stop the local chime; the WS broadcast syncs other screens.
    setSosBanner((b) => (b && b.sos_id === sosId ? { ...b, acknowledged: true } : b));
    try { await api.post(`/admin/sos/${sosId}/acknowledge`, {}); } catch (e) { /* best-effort */ }
  }, []);

  // Repeat the chime every 20s ONLY while an SOS banner is unacknowledged.
  // Stops the moment a dispatcher acknowledges/opens it; re-arms on escalation.
  useEffect(() => {
    if (chimeRef.current) { clearInterval(chimeRef.current); chimeRef.current = null; }
    if (sosBanner && !sosBanner.acknowledged) {
      chimeRef.current = setInterval(playSosChime, 20000);
    }
    return () => { if (chimeRef.current) { clearInterval(chimeRef.current); chimeRef.current = null; } };
  }, [sosBanner?.sos_id, sosBanner?.acknowledged, playSosChime]);
  const [corpPending, setCorpPending] = useState(0);
  const pollRef = useRef(null);
  const healthPollRef = useRef(null);
  const fatiguePollRef = useRef(null);
  const compliancePollRef = useRef(null);

  /* ============================ Data fetchers ============================ */
  const fetchAll = useCallback(async () => {
    // Resilient load: a single failing endpoint must NOT blank the entire
    // dashboard. Previously this used Promise.all + a silent catch, so ANY
    // one rejected call (e.g. a slow/erroring sibling) left every list empty
    // — including Customers showing "0 registered" while data existed.
    const calls = [
      ["stats", "/admin/stats", setStats],
      ["drivers", "/admin/drivers", setDrivers],
      ["passengers", "/admin/passengers", setPassengers],
      ["rides", "/admin/rides" + (statusFilter ? `?status=${statusFilter}` : ""), setRides],
      ["fareConfig", "/admin/fare-config", setFareConfig],
      ["surgeConfig", "/admin/surge-config", setSurgeConfig],
      ["cycleConfig", "/admin/cycle-config", setCycleConfig],
      ["cycles", "/admin/cycles", setCycles],
      ["fixedRoutes", "/admin/fixed-routes", setFixedRoutes],
      ["accountRides", "/admin/account-rides", setAccountRides],
    ];
    const results = await Promise.allSettled(calls.map(([, url]) => api.get(url)));
    results.forEach((res, i) => {
      const [name, , setter] = calls[i];
      if (res.status === "fulfilled") {
        setter(res.value.data);
      } else {
        console.warn(`[admin] fetch failed for ${name}:`, res.reason?.response?.status, res.reason?.message);
      }
    });
  }, [statusFilter]);

  const fetchHealth = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/ride-health");
      setHealthData(data);
    } catch (e) { /* ignore */ }
  }, []);

  const fetchFatigueAlert = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/fatigue/alert-count");
      setFatigueAlert(data);
    } catch (e) { /* ignore */ }
  }, []);

  const fetchSosActive = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/sos/active-count");
      setSosActive(data);
    } catch (e) { /* ignore */ }
  }, []);

  const fetchComplianceAlert = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/compliance/alert-count");
      setComplianceAlert(data);
    } catch (e) { /* ignore */ }
  }, []);

  const fetchInboxUnread = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/whatsapp/unread-count");
      setInboxUnread(data.unread || 0);
    } catch (e) { /* ignore */ }
  }, []);

  const fetchCorpPending = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/corporate/applications/pending-count");
      setCorpPending(data.pending || 0);
    } catch (e) { /* ignore */ }
  }, []);

  /* ============================ Realtime ============================ */
  useRealtime(tokenStore.get(), (msg) => {
    if (msg.type === "driver_location") {
      setDrivers((prev) =>
        prev.map((d) =>
          d.id === msg.driver_id ? { ...d, current_lat: msg.lat, current_lng: msg.lng } : d
        )
      );
    }
    if (msg.type === "sos_alert") {
      fetchSosActive();
      setSosSignal({ ts: Date.now(), sos_id: msg.sos_id, action: msg.action });
      if (msg.action === "raised" || msg.action === "escalated") {
        // Prominent persistent visual alert + immediate chime. Escalation
        // re-arms the chime even if a previous state was acknowledged.
        playSosChime();
        setSosBanner({
          sos_id: msg.sos_id, driver_name: msg.driver_name, level: msg.level,
          action: msg.action, ts: msg.ts || new Date().toISOString(), acknowledged: false,
        });
      }
      if (msg.action === "acknowledged") {
        // Any dispatcher acknowledging stops the repeated chime everywhere;
        // the banner stays visible until the incident is resolved.
        setSosBanner((b) => (b && b.sos_id === msg.sos_id ? { ...b, acknowledged: true } : b));
      }
      if (msg.action === "resolved") {
        setSosBanner((b) => (b && b.sos_id === msg.sos_id ? null : b));
      }
      if (msg.action === "driver_reply") {
        playSosChime();
        toast.error(
          `💬 Driver reply — ${msg.driver_name || "a driver"}: ${msg.message || ""}`,
          { duration: 30000, action: { label: "Open Safety", onClick: () => setTab("safety") } });
      } else if (msg.action === "escalated") {
        toast.error(
          `⏱️ ESCALATED — no response from ${msg.driver_name || "driver"}. Review immediately.`,
          { duration: 45000, action: { label: "Open Safety", onClick: () => setTab("safety") } });
      } else if (msg.action === "raised") {
        toast.error(
          `🚨 ${msg.level === "emergency" ? "SILENT EMERGENCY" : "ASSISTANCE REQUESTED"} — ${msg.driver_name || "a driver"}`,
          { duration: 30000, action: { label: "Open Safety", onClick: () => setTab("safety") } });
      } else if (msg.action === "contacts_notified") {
        toast.message(`📇 Emergency contacts notified for ${msg.driver_name || "driver"}.`);
      }
    }
    if (msg.type === "ride_health_alert") {
      // Live toast for any operational alert emitted by the janitor. The
      // RideHealthPanel itself polls every 15s so the table refreshes on
      // its own — the toast just makes sure the operator notices RIGHT
      // NOW even when they're on a different tab.
      fetchHealth();  // pull fresh data so the badge updates immediately
      const toaster =
        msg.severity === "error" ? toast.error :
        msg.severity === "warning" ? toast.warning :
        toast;
      toaster(msg.message || "Operational alert", {
        description: msg.kind ? msg.kind.replace(/_/g, " ") : undefined,
        action: msg.kind === "ride_stuck" || msg.kind === "orphan_recovery"
          ? { label: "Open Health", onClick: () => setTab("health") }
          : undefined,
      });
    }
    if (msg.type === "web_booking_ack") {
      // Another dispatcher claimed/released ownership — refresh the queue so
      // every Control Room screen reflects current ownership in real time.
      setWebAckSignal((n) => n + 1);
    }
    if (msg.type === "web_booking_request") {
      // Real-time Control Room alert for a customer-submitted website AI
      // booking request. Notification only — nothing is booked or dispatched;
      // a dispatcher must still review & confirm it.
      const when = msg.scheduled_for && msg.scheduled_for !== "asap"
        ? new Date(msg.scheduled_for).toLocaleString("en-GB", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
        : "ASAP";
      toast.custom((id) => (
        <div className="w-[360px] rounded-xl border border-[#C7D2FE] bg-white shadow-2xl p-3.5" data-testid="web-booking-toast">
          <div className="flex items-center gap-2 font-bold text-[#3730A3] text-sm">
            🌐 New web booking {msg.reference || ""}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {(msg.customer_name || "Customer")} · {(msg.pickup || "?")} → {(msg.dropoff || "?")} · {when}
          </div>
          <div className="flex items-center gap-2 mt-3">
            <button
              onClick={() => { setTab("ai-booking"); setWebReqFocus({ session_id: msg.session_id, ts: Date.now() }); toast.dismiss(id); }}
              className="flex-1 h-8 rounded-lg bg-[#002FA7] hover:bg-[#001f6e] text-white text-xs font-bold"
              data-testid="web-booking-toast-review"
            >
              Review request
            </button>
            <button
              onClick={() => { acknowledgeWebRequest(msg.session_id, msg.customer_name); toast.dismiss(id); }}
              className="flex-1 h-8 rounded-lg bg-[#F59E0B] hover:bg-[#D97706] text-white text-xs font-bold"
              data-testid="web-booking-toast-ack"
            >
              Acknowledge
            </button>
          </div>
        </div>
      ), { duration: 30000 });
    }
  });
  useEffect(() => {
    if (loading) return;
    if (!user) navigate("/login");
    else if (user.role !== "admin") {
      navigate(user.role === "driver" ? "/driver" : "/passenger", { replace: true });
    }
  }, [user, loading, navigate]);

  /* ============================ Polling ============================ */
  useEffect(() => {
    if (!user) return;
    fetchAll();
    pollRef.current = setInterval(fetchAll, 4000);
    fetchHealth();
    healthPollRef.current = setInterval(fetchHealth, 15000);
    fetchFatigueAlert();
    fatiguePollRef.current = setInterval(fetchFatigueAlert, 60000);
    fetchComplianceAlert();
    compliancePollRef.current = setInterval(fetchComplianceAlert, 60000);
    fetchInboxUnread();
    const inboxPoll = setInterval(fetchInboxUnread, 20000);
    fetchCorpPending();
    const corpPoll = setInterval(fetchCorpPending, 30000);
    fetchSosActive();
    const sosPoll = setInterval(fetchSosActive, 15000);
    return () => {
      clearInterval(pollRef.current);
      clearInterval(healthPollRef.current);
      clearInterval(fatiguePollRef.current);
      clearInterval(compliancePollRef.current);
      clearInterval(inboxPoll);
      clearInterval(corpPoll);
      clearInterval(sosPoll);
    };
  }, [user, fetchAll, fetchHealth, fetchFatigueAlert, fetchComplianceAlert, fetchInboxUnread, fetchCorpPending, fetchSosActive]);

  // Unread-counter for the Health tab badge: red dot only when there's
  // something actually requiring attention (stuck rides or active
  // disconnect storms in 24h).
  const healthBadge = healthData
    ? (healthData.currently_stuck_rides?.length || 0) +
      (healthData.ws_disconnect_clusters_24h?.length || 0)
    : 0;

  // Fatigue tab badge: drivers approaching/over their Condition 22 limits.
  // Red when any driver is OVER, amber when only approaching.
  const fatigueBadge = fatigueAlert ? (fatigueAlert.total || 0) : 0;
  const fatigueTone = fatigueAlert && fatigueAlert.over > 0 ? "over" : "approaching";

  // Compliance tab badge: documents expiring/expired. Red when any are
  // already expired, amber when only expiring within 30 days.
  const complianceBadge = complianceAlert ? (complianceAlert.total || 0) : 0;
  const complianceTone = complianceAlert && complianceAlert.expired > 0 ? "expired" : "expiring";

  /* ============================ Mutations ============================ */
  const m = useAdminMutations({
    fetchAll, fetchHealth,
    setFareConfig, setSurgeConfig, setCycleConfig,
  });

  /* ============================ Render ============================ */
  // onOpen maps a home tile → an existing tab id (+ optional Comms sub-tab),
  // so every route/panel/permission/badge is preserved.
  const openTab = (target, commsTab) => {
    if (commsTab) setCommsInitial(commsTab);
    setTab(target);
  };
  const badges = {
    rides: stats?.active_rides || 0,
    health: healthBadge,
    inbox: inboxUnread,
    fatigue: { count: fatigueBadge, tone: fatigueTone },
    compliance: { count: complianceBadge, tone: complianceTone },
    corpApps: corpPending,
    safety: sosActive,
  };

  return (
    <FinancialPrivacyProvider>
    <div className="min-h-screen bg-background flex flex-col" data-testid="admin-page">
      {sosBanner && (
        <div className="fixed inset-x-0 top-0 z-[200] px-3 pt-3 pointer-events-none" data-testid="sos-alert-overlay">
          <div className={`pointer-events-auto mx-auto max-w-4xl rounded-xl shadow-2xl border-2 px-4 py-3 flex items-center gap-3 ${!sosBanner.acknowledged ? "animate-pulse" : ""} ${
            sosBanner.level === "emergency"
              ? "bg-[#DC2626] border-[#7F1D1D] text-white"
              : "bg-[#B45309] border-[#78350F] text-white"}`}>
            <div className="shrink-0 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-black text-sm sm:text-base uppercase tracking-wide leading-tight" data-testid="sos-alert-title">
                {sosBanner.action === "escalated" ? "⏱ SOS ESCALATED — NO DRIVER RESPONSE" : (sosBanner.level === "emergency" ? "🚨 SILENT EMERGENCY" : "🚨 ASSISTANCE REQUESTED")}
              </div>
              <div className="text-xs sm:text-sm opacity-95 truncate" data-testid="sos-alert-meta">
                {sosBanner.driver_name || "A driver"} · {sosBanner.level === "emergency" ? "Emergency" : "Assistance"} · {new Date(sosBanner.ts).toLocaleTimeString("en-GB")}
                {sosBanner.acknowledged ? " · ✅ Acknowledged" : ""}
              </div>
            </div>
            {!sosBanner.acknowledged && (
              <button onClick={() => acknowledgeSos(sosBanner.sos_id)} data-testid="sos-alert-ack-btn"
                className="shrink-0 bg-white/20 hover:bg-white/30 text-white font-semibold text-xs sm:text-sm rounded-lg px-3 py-2">
                Acknowledge
              </button>
            )}
            <button onClick={() => { acknowledgeSos(sosBanner.sos_id); setTab("safety"); setOpenSosId(sosBanner.sos_id); }}
              data-testid="sos-alert-open-btn"
              className="shrink-0 bg-white text-[#0A0A0A] font-bold text-xs sm:text-sm rounded-lg px-3 sm:px-4 py-2 hover:bg-white/90 flex items-center gap-1.5">
              <ShieldAlert className="w-4 h-4" /> Open SOS
            </button>
            <button onClick={() => setSosBanner(null)} data-testid="sos-alert-dismiss-btn"
              className="shrink-0 p-1.5 rounded-lg hover:bg-white/20" aria-label="Dismiss">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      <Header title="CONTROL ROOM" accent="blue" />
      <AdminStatsBar stats={stats} />
      {tab !== "home" && <AdminSectionBar tab={tab} onHome={() => setTab("home")} />}

      <div className="flex-1">
        {tab === "home" && (
          <>
            {user?.is_platform_owner && (
              <div className="px-4 pt-4" data-testid="platform-owner-banner">
                <button
                  data-testid="open-platform-console"
                  onClick={() => navigate("/platform")}
                  className="w-full flex items-center justify-between gap-3 rounded-xl border border-[#002FA7]/20 bg-[#002FA7]/5 hover:bg-[#002FA7]/10 transition-colors px-4 py-3 text-left"
                >
                  <span className="flex items-center gap-3">
                    <Building2 className="w-5 h-5 text-[#002FA7]" />
                    <span>
                      <span className="block text-sm font-bold text-foreground">Platform Console</span>
                      <span className="block text-xs text-muted-foreground">Manage operator tenants &amp; applications (super admin)</span>
                    </span>
                  </span>
                  <span className="label-mini text-[#002FA7]">OPEN →</span>
                </button>
              </div>
            )}
            <AdminHomeDashboard stats={stats} badges={badges} onOpen={openTab} />
          </>
        )}
        {tab === "map" && <MapPanel drivers={drivers} rides={rides} />}
        {tab === "dashboard" && (
          <KpiDashboardPanel
            onOpenRide={() => { setStatusFilter("completed"); setTab("rides"); }}
          />
        )}

        {tab === "readiness" && <LaunchReadinessPanel />}
        {tab === "copilot" && <CopilotPanel />}
        {tab === "ai-booking" && <AiBookingPanel onCreateBooking={m.createBooking} focusRequest={webReqFocus} currentUser={user} ackSignal={webAckSignal} />}
        {tab === "voice-tuning" && <VoiceTuningPanel />}
        {tab === "demo" && (
          <DemoPanel
            kioskEnabled={kioskEnabled} setKioskEnabled={setKioskEnabled}
            kioskTimeoutMin={kioskTimeoutMin} setKioskTimeoutMin={setKioskTimeoutMin}
            kioskReset={kioskReset} setKioskReset={setKioskReset}
          />
        )}

        {tab === "rides" && (
          <RidesPanel
            rides={rides}
            drivers={drivers}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            onRedispatch={m.redispatchRide}
            onForceCancel={m.forceCancelRide}
            onForceComplete={m.forceCompleteRide}
            onAssign={m.assignDriver}
            onCreateBooking={m.createBooking}
          />
        )}

        {tab === "fleet" && (
          <FleetPanel
            fleetTab={fleetTab}
            setFleetTab={setFleetTab}
            drivers={drivers}
            passengers={passengers}
            cycles={cycles}
            accountRides={accountRides}
            rides={rides}
            onDelete={m.deleteUser}
            onToggleAccount={m.toggleAccountCustomer}
            onRefresh={fetchAll}
          />
        )}

        {tab === "safety" && <SafetyPanel sosSignal={sosSignal} openSosId={openSosId} onConsumeOpen={() => setOpenSosId(null)} />}

        {tab === "customers" && (
          <PassengersPanel
            passengers={passengers}
            onToggleAccount={m.toggleAccountCustomer}
            onDelete={m.deleteUser}
            onSaveCustomer={m.saveCustomer}
          />
        )}

        {tab === "approvals" && <ApprovalsPanel />}
        {tab === "onboarding" && <OnboardingWizard />}

        {tab === "imports" && <ImportsPanel />}

        {tab === "health" && (
          <RideHealthPanel
            data={healthData}
            lastFetchedAt={healthData ? new Date() : null}
            onRedispatch={m.wrappedRedispatch}
            onForceCancel={m.wrappedForceCancel}
            onForceComplete={m.wrappedForceComplete}
            onResolve={m.resolveHealthFlag}
          />
        )}

        {tab === "system" && <SystemHealthPanel />}
        {tab === "fatigue" && <FatiguePanel />}
        {tab === "compliance" && <CompliancePanel />}
        {tab === "corporate" && (
          <CorporatePanel passengers={passengers} onRefreshPassengers={fetchAll} />
        )}
        {tab === "corp-apps" && <CorporateApplicationsPanel />}
        {tab === "subscriptions" && <SubscriptionsPanel />}
        {tab === "inbox" && <WhatsAppInboxPanel />}
        {tab === "confirmations" && <ConfirmationLogPanel />}
        {tab === "missed-calls" && <MissedEnquiriesPanel />}
        {tab === "comms" && <CommsPanel key={commsInitial} initialTab={commsInitial} />}

        {SHOW_LAUNCH_TOOLS && tab === "provisioning" && <OperatorProvisioningWizard />}
        {SHOW_LAUNCH_TOOLS && tab === "launch" && <LaunchResetPanel />}

        {tab === "settings" && (
          <SettingsPanel
            settingsTab={settingsTab}
            setSettingsTab={setSettingsTab}
            fareConfig={fareConfig}
            surgeConfig={surgeConfig}
            cycleConfig={cycleConfig}
            fixedRoutes={fixedRoutes}
            onUpdateFare={m.updateFare}
            onUpdateSurge={m.updateSurge}
            onUpdateCycle={m.updateCycle}
            onSaveRoute={m.saveFixedRoute}
            onDeleteRoute={m.deleteFixedRoute}
          />
        )}
      </div>

      {SHOW_DEMO_TOOLS && (
        <>
          {/* Preview-only Demo Tools launcher. Auto-hidden while any modal /
              dialog / drawer is open so it never obscures interactive controls
              (AI Booking, New Booking, SOS, Corporate, etc.). Collapsed to a
              compact pill by default to stay out of the operator's way. */}
          {!tourOpen && !presentOpen && !anyModalOpen && (
            <div className="fixed bottom-5 right-5 z-[70] flex flex-col items-end gap-2" data-testid="demo-tools">
              {demoExpanded && (
                <>
                  <button
                    onClick={() => { setPresentInitialProfile(null); setPresentOpen(true); }}
                    className="px-4 py-2.5 rounded-full bg-[#6D28D9] text-white text-sm font-semibold shadow-xl flex items-center gap-2 hover:bg-[#5b21b6] transition-colors"
                    data-testid="start-presentation"
                  >
                    <PresentationIcon className="w-4 h-4" /> AI Presentation
                  </button>
                  <button
                    onClick={() => setTourOpen(true)}
                    className="px-4 py-2 rounded-full bg-white text-[#6D28D9] border border-[#6D28D9] text-sm font-semibold shadow-lg flex items-center gap-2 hover:bg-[#6D28D9]/[0.05] transition-colors"
                    data-testid="start-guided-tour"
                  >
                    <Compass className="w-4 h-4" /> Guided Tour
                  </button>
                </>
              )}
              <button
                onClick={() => setDemoExpanded((v) => !v)}
                className={`px-3.5 py-2 rounded-full text-xs font-bold uppercase tracking-wide shadow-lg flex items-center gap-1.5 transition-colors ${demoExpanded ? "bg-foreground text-white" : "bg-[#6D28D9] text-white hover:bg-[#5b21b6]"}`}
                data-testid="demo-tools-toggle"
                aria-expanded={demoExpanded}
                title="Preview demo tools"
              >
                {demoExpanded ? <X className="w-3.5 h-3.5" /> : <Sparkles className="w-3.5 h-3.5" />}
                Demo Tools
              </button>
            </div>
          )}
          <PresentationWelcome open={welcomeOpen} onPick={handleWelcomePick} onExplore={handleWelcomeExplore} />
          <GuidedTour open={tourOpen} setTab={setTab} onClose={() => setTourOpen(false)} />
          <PresentationMode
            open={presentOpen}
            setTab={setTab}
            initialProfileId={presentInitialProfile}
            onPlayingChange={setPresentationPlaying}
            onClose={() => { setPresentOpen(false); setPresentInitialProfile(null); }}
          />
        </>
      )}
    </div>
    </FinancialPrivacyProvider>
  );
}
